import React, { useState, useEffect } from 'react'; 
import '../App.css'; 
import axios from 'axios';
import { useSession } from '../Cart-Context/session'; 
import { trackEvent } from '../utils/trackEvents'; 
import { API_BASE_URL } from '../utils/apiConfig.js';

const About = () => {
  const { sessionId } = useSession(); // Get the current sessionId

  // State for form fields
  const [contactData, setContactData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });

  // State for the submission status message
  const [status, setStatus] = useState('');

  // Track when the user arrives at the About page
  useEffect(() => {
    trackEvent(sessionId, 'PAGE_VIEW', 'About Page');
  }, [sessionId]);

  const handleChange = (e) => {
    setContactData({ ...contactData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setStatus('Sending...');

    try {
      await axios.post(`${API_BASE_URL}/api/contact`, contactData);
      setStatus('Thank you! Your message has been sent.');
      setContactData({ firstName: '', lastName: '', email: '', message: '' }); // Clear form
    } catch (error) {
      console.error(error);
      setStatus('Failed to send message. Please try again later.');
    }
  };
  
  return (
    <main className="page-container">
      <h1 className="page-title">About</h1>

      <div className="about-container">
        {/*The Purpose and Data sections*/}
        <section className="about-section">
          <h2 className="about-heading">Our Purpose</h2>
          <p>
            The goal of this website is to serve as a data collection resource that mimics an online food retail environment. The primary motivation is to study food choice behavior.
          </p>
          <p>
            While diet-related diseases have been rising, the main attempts to solve this have focused on information or pricing. This project aims to use the rise of online food retail to explore new technological options that can help promote healthier food choices.
          </p>
          <p>
            To do this, the platform is designed to be highly customizable for researchers. They can:
          </p>
          <ul className="about-list">
            <li className="about-list-item">Change elements of product presentation, like what categories or information is shown.</li>
            <li className="about-list-item">Establish how participants can interact with the store, such as using filters or search tools.</li>
            <li className="about-list-item">Determine how participants make choices, like allowing one item or many.</li>
          </ul>
        </section>

        <section className="about-section">
          <h2 className="about-heading">What Data We're Looking At</h2>
          <p>
            A critical part of this project is generating data on research participants' behaviors and choices. The types of data being recorded include:
          </p>
          <ul className="about-list">
            <li className="about-list-item">The specific food items that participants choose.</li>
            <li className="about-list-item">Actions taken to find items, including any search terms used.</li>
            <li className="about-list-item">How participants use tools to change what they see, such as filter settings or sorting choices.</li>
            <li className="about-list-item">The amount of time participants spend making their choices.</li>
            <li className="about-list-item">Differences in experiment conditions, such as how one group's choices compare to another's if they were given different tools.</li>
          </ul>
        </section>

        <section className="about-section">
          <h2 className="about-heading">Contact Us</h2>
          <p className="contact-form-intro">
            If you have any questions or concerns, fill out this form below and we will get back to you shortly.
          </p>

          <form className="contact-form" onSubmit={handleFormSubmit}>
            <div className="form-row">
              <div className="form-group-half">
                <label htmlFor="firstName">First Name</label>
                <input 
                  type="text" 
                  id="firstName" 
                  name="firstName" 
                  value={contactData.firstName} 
                  onChange={handleChange} 
                  required 
                />
              </div>
              <div className="form-group-half">
                <label htmlFor="lastName">Last Name</label>
                <input 
                  type="text" 
                  id="lastName" 
                  name="lastName" 
                  value={contactData.lastName} 
                  onChange={handleChange} 
                  required 
                />
              </div>
            </div>

            <div className="form-group-full">
              <label htmlFor="email">Email</label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                value={contactData.email} 
                onChange={handleChange} 
                required 
              />
            </div>

            <div className="form-group-full">
              <label htmlFor="message">Question/Concern</label>
              <textarea 
                id="message" 
                name="message" 
                rows="6" 
                value={contactData.message} 
                onChange={handleChange} 
                required
              ></textarea>
            </div>

            <button type="submit" className="contact-submit-btn">
              Submit
            </button>
            
            {status && (
              <p style={{ 
                marginTop: '1rem', 
                color: status.includes('Failed') ? '#d93434' : '#056621', 
                fontWeight: '600' 
              }}>
                {status}
              </p>
            )}
          </form>
        </section>
      </div>
    </main>
  );
};

export default About;